import { IonButton } from '@ionic/react';
import type { ReactNode } from 'react';

import { Icon, type RxIconName } from '~/components/ui/Icon';
import { type WizardStep, useWizard } from '~/pages/emitir/wizardContext';

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Tomador',
  2: 'Serviço',
  3: 'Valor',
  4: 'Revisão',
};

interface WizardLayoutProps {
  children: ReactNode;
  eyebrow: string;
  title: string;
  description?: string;
  canAdvance: boolean;
  advanceLabel?: string;
  advanceIcon?: RxIconName;
  isAdvancing?: boolean;
  onAdvance(): void;
  onBack(): void;
}

export function WizardLayout({
  children,
  eyebrow,
  title,
  description,
  canAdvance,
  advanceLabel = 'Continuar',
  advanceIcon = 'arrow_right_long',
  isAdvancing = false,
  onAdvance,
  onBack,
}: WizardLayoutProps): JSX.Element {
  const { step } = useWizard();

  return (
    <div
      className="rx-container rx-screen"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        paddingBottom: 28,
      }}
    >
      <div style={{ padding: '12px 24px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <button type="button" className="rx-iconbtn" onClick={onBack} aria-label="Voltar">
            <Icon name="back" size={18} />
          </button>
          <span
            className="rx-mono"
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              color: 'var(--rx-text-3)',
            }}
          >
            Passo {step} / 4
          </span>
        </div>
      </div>

      <StepProgress current={step} />

      <header style={{ padding: '24px 24px 0' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--rx-text-3)',
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            margin: '10px 0 0',
            fontSize: 30,
            fontWeight: 500,
            color: 'var(--rx-text)',
            letterSpacing: '-0.7px',
            lineHeight: 1.1,
          }}
        >
          {title}
        </h1>
        {description && (
          <p
            style={{
              marginTop: 10,
              fontSize: 14,
              color: 'var(--rx-text-2)',
              lineHeight: 1.45,
              maxWidth: '38ch',
            }}
          >
            {description}
          </p>
        )}
      </header>

      <main style={{ flex: 1, padding: '20px 24px 0' }}>{children}</main>

      <footer style={{ padding: '24px 24px 0' }}>
        <IonButton
          className="rx-button"
          expand="block"
          disabled={!canAdvance || isAdvancing}
          onClick={onAdvance}
        >
          {isAdvancing ? (
            'Enviando…'
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {advanceLabel}
              <Icon name={advanceIcon} size={16} color="var(--rx-brand-ink)" />
            </span>
          )}
        </IonButton>
      </footer>
    </div>
  );
}

interface StepProgressProps {
  current: WizardStep;
}

function StepProgress({ current }: StepProgressProps): JSX.Element {
  const steps: WizardStep[] = [1, 2, 3, 4];
  return (
    <div style={{ padding: '16px 24px 0' }}>
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {steps.map((value) => {
          const isCurrent = value === current;
          const isDone = value < current;
          const trackColor = isCurrent
            ? 'var(--rx-brand)'
            : isDone
              ? 'var(--rx-brand-2)'
              : 'var(--rx-border-strong)';
          const labelColor = isCurrent
            ? 'var(--rx-brand)'
            : isDone
              ? 'var(--rx-brand-2)'
              : 'var(--rx-text-3)';
          return (
            <li key={value} style={{ flex: 1 }}>
              <div
                style={{
                  height: 3,
                  borderRadius: 3,
                  background: trackColor,
                  transition: 'background 200ms ease',
                }}
                aria-current={isCurrent ? 'step' : undefined}
              />
              <div
                className="rx-mono"
                style={{
                  marginTop: 6,
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: labelColor,
                }}
              >
                {STEP_TITLES[value]}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
